import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Pressable, StyleSheet, Text, useWindowDimensions, View, type LayoutChangeEvent } from "react-native";
import { BAND_GLYPH, NO_LOAD, type IntensityBand } from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import { TEXT_FIELD_BOX_H, TextField } from "../primitives/TextField";
import type { UiTheme } from "../theme";

/** The least height of a body row (the 44pt tap floor). */
export const ROW_MIN = 44;
/** The borders a row's box adds to its content: the hairline, and the cell's ring. */
export const LINE = 1;
export const RING = 2;

/** One cell as the grid reads it: its lens text (null: not in that round),
 *  its band, whether it is by hand, open, and what a screen reader says. */
export type RoundCell = {
  text: string | null;
  band: IntensityBand | null;
  byHand: boolean;
  open: boolean;
  spoken: string;
};

// One cell of the wide day grid (#336 slice 6). The band paints a box inside
// the cell's ring, so the column shows between two cells of the same band
// and each round reads apart (brief §15 row 28). The glyph and the «by hand»
// mark have a line of their own above the value: apart at any font size. The
// open cell's ring is ink, with a surface edge inside it: one of the two
// reads at 3:1 on every fill. One shell; the «Sets» lens (row 36) only swaps
// the value for a field, in a plain View, so no button wraps an input.
export function RoundGridCell({
  theme,
  cell,
  height,
  onLayout,
  onPress,
  sets,
}: {
  theme: UiTheme;
  cell: RoundCell;
  height: number;
  /** The content's height, for the row to take the tallest. */
  onLayout: (e: LayoutChangeEvent) => void;
  onPress: () => void;
  /** The «Sets» lens: the count typed in, written on Enter or blur. */
  sets?: { count: number; a11yLabel: string; onCommit: (n: number) => void };
}) {
  const s = useMemo(() => makeStyles(theme), [theme]);
  const p = theme.palette;
  const scale = cell.band ? theme.intensity[cell.band] : null;
  const ink = cell.text === null ? p.ink3 : cell.byHand ? (scale?.on ?? p.warn) : (scale?.on ?? p.ink);
  const field = sets && cell.text !== null;
  const child = field ? (
    <SetCountField theme={theme} count={sets.count} label={sets.a11yLabel} onCommit={sets.onCommit} onFocus={onPress} />
  ) : (
    <Text style={[s.value, { color: ink }, cell.byHand && s.hand]}>{cell.text ?? NO_LOAD}</Text>
  );
  const body = (pressed: boolean): ReactNode => (
    <>
      {scale ? (
        <View
          pointerEvents="none"
          style={[s.fill, { backgroundColor: scale.fill }, cell.open && s.fillOpen, pressed && s.dim]}
        />
      ) : null}
      <View onLayout={onLayout} style={s.content}>
        {cell.band || cell.byHand ? (
          <View style={s.marks} importantForAccessibility="no-hide-descendants" accessibilityElementsHidden>
            <Text style={[s.glyph, { color: ink }]}>{cell.band ? BAND_GLYPH[cell.band] : ""}</Text>
            {cell.byHand ? <Icon name="edit" size={theme.typography.sizes.sm} color={ink} /> : null}
          </View>
        ) : null}
        {child}
      </View>
    </>
  );
  return (
    <View style={[s.row, { height }]}>
      {field ? (
        <View style={[s.cell, cell.open && s.open]}>{body(false)}</View>
      ) : (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={cell.spoken}
          accessibilityState={{ selected: cell.open }}
          onPress={onPress}
          style={({ pressed }) => [s.cell, pressed && !scale && s.pressed, cell.open && s.open]}
        >
          {({ pressed }) => body(pressed)}
        </Pressable>
      )}
    </View>
  );
}

// The count as typed. Enter writes it (on web too, where blur may not
// follow), and so does leaving the field; a number already sent is not sent
// twice. Empty, 0 or not a number: nothing is written. Either way the text
// goes back to the count the round has, and a write that lands brings the new.
function SetCountField({
  theme,
  count,
  label,
  onCommit,
  onFocus,
}: {
  theme: UiTheme;
  count: number;
  label: string;
  onCommit: (n: number) => void;
  onFocus: () => void;
}) {
  const { fontScale } = useWindowDimensions();
  const s = useMemo(() => makeFieldStyles(theme, fontScale), [theme, fontScale]);
  const [text, setText] = useState(String(count));
  const sent = useRef<number | null>(null);
  useEffect(() => {
    setText(String(count));
    sent.current = null;
  }, [count]);
  const commit = () => {
    const n = /^\d+$/.test(text.trim()) ? Number(text.trim()) : 0;
    if (n >= 1 && n !== count && n !== sent.current) {
      sent.current = n;
      onCommit(n);
    }
    setText(String(count));
  };
  return (
    <TextField
      theme={theme}
      numeric
      keyboardType="number-pad"
      maxLength={2}
      selectTextOnFocus
      value={text}
      onChangeText={setText}
      onFocus={() => {
        // A new edit: a number refused before may be sent again.
        sent.current = null;
        onFocus();
      }}
      onSubmitEditing={commit}
      onBlur={commit}
      returnKeyType="done"
      accessibilityLabel={label}
      hitSlop={FIELD_SLOP}
      style={s.field}
    />
  );
}

// The compact box (the TextField default), up to the 44 floor by hitSlop on
// native; on web the 44pt row around it is the floor.
const SLOP = (ROW_MIN - TEXT_FIELD_BOX_H) / 2;
const FIELD_SLOP = { top: SLOP, bottom: SLOP, left: SLOP, right: SLOP };
const DIGITS = 2;
const BORDER = 1;

function makeFieldStyles(theme: UiTheme, fontScale: number) {
  const { spacing: sp, typography: ty } = theme;
  const pad = sp.s2;
  // The ceiling is the content of the narrowest round column (72 less ring and
  // padding), which «20» fits at a font of 1.5; the floor is the square box.
  const ceiling = sp.s8 + sp.s5;
  // Two mono digits at the text size now in force, plus padding and border:
  // it grows with the font.
  const fit = Math.ceil(DIGITS * ty.monoAdvanceEm * ty.sizes.base * fontScale) + 2 * pad + 2 * BORDER;
  return StyleSheet.create({
    field: {
      width: Math.min(Math.max(fit, TEXT_FIELD_BOX_H), ceiling),
      paddingHorizontal: pad,
      fontFamily: ty.fonts.mono,
      alignSelf: "center",
    },
  });
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // The row's separator is this box's, so the cell's ring keeps four sides.
    row: { minHeight: ROW_MIN, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: p.border },
    cell: {
      flex: 1,
      justifyContent: "center",
      paddingHorizontal: s.s1,
      // The open cell's ring takes no room: every cell has it, unseen.
      borderWidth: RING,
      borderColor: "transparent",
    },
    // Inside the ring: the transparent ring is the gap between two fills.
    fill: { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, borderRadius: r.r2 },
    fillOpen: { borderWidth: RING, borderColor: p.surface, borderRadius: 0 },
    dim: { opacity: 0.8 },
    content: { paddingVertical: s.s1 },
    marks: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
    glyph: { fontSize: ty.sizes.sm, fontWeight: "800" },
    pressed: { backgroundColor: p.surface2 },
    open: { borderColor: p.ink },
    value: { fontSize: ty.sizes.sm, fontFamily: ty.fonts.mono, textAlign: "center" },
    hand: { fontWeight: "700" },
  });
}
